import React, { useEffect, useState } from "react";
import axios from "axios";
import { Col, Row } from "antd";
import moment from "moment";
import { BaseUrlRace } from "../Api/BaseUrl";
import JsBarcode from "jsbarcode-fixed";

const getPrintAddress = (item) => {
  const spNumber = item?.sp || item?.so || item?.sm || "";
  const isRoSp = typeof spNumber === "string" && spNumber.startsWith("21-RO-");

  if (isRoSp) {
    return (
      item?.idAlmuat?.alamat ||
      item?.detail?.[0]?.idAlmuat?.alamat ||
      item?.alamatMuat?.alamat ||
      item?.detail?.[0]?.alamatMuat?.alamat ||
      item?.detail?.[0]?.alamatSekolah ||
      item?.alamatBongkar ||
      item?.alamat ||
      "-"
    );
  }

  return item?.detail?.[0]?.alamatSekolah || item?.alamatBongkar || item?.alamat || "-";
};

const hasValue = (value) =>
  value !== undefined && value !== null && `${value}`.trim() !== "" && value !== "-";

const pickValue = (...values) => {
  const value = values.find(hasValue);
  return value === undefined ? "-" : value;
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const getSchoolName = (detailItem, parentItem = {}) =>
  pickValue(
    detailItem?.sekolahTujuan,
    detailItem?.sekolahtujuan,
    detailItem?.sekolah,
    detailItem?.nama_sekolah,
    detailItem?.penerima,
    detailItem?.pic,
    detailItem?.alamatMuat?.pic,
    detailItem?.idAlmuat?.pic,
    parentItem?.sekolahTujuan,
    parentItem?.sekolahtujuan,
    parentItem?.sekolah,
    parentItem?.nama_sekolah,
    parentItem?.penerima,
    parentItem?.pic
  );

const getMemoText = (detailItem, parentItem = {}, fallback = "-") => {
  const memo = pickValue(
    detailItem?.SjErlangga,
    parentItem?.SjErlangga,
    detailItem?.memo,
    parentItem?.memo,
    detailItem?.sm,
    parentItem?.sm,
    parentItem?.so,
    parentItem?.sp
  );

  return memo === "-" ? fallback : memo;
};

const getPrintRows = (item) => {
  const detail = Array.isArray(item?.detail) && item.detail.length > 0 ? item.detail : [item];

  return detail.map((detailItem) => ({
    ...detailItem,
    sekolahTujuan: getSchoolName(detailItem, item),
    memoText: getMemoText(detailItem, item, "FPPBRNYA TIDAK ADA NOMORNYA"),
    koli: toNumber(pickValue(detailItem?.koli, item?.koli, 0)),
    ikat: toNumber(pickValue(detailItem?.ikat, item?.ikat, 0)),
  }));
};

function PrintRO() {
  const [dataDetail, setDataDetail] = useState(null); // State for storing detail data
  const [loading, setLoading] = useState(true); // Loading state
  const idMp = localStorage.getItem("idMp"); // Get idMp from localStorage
  const hariini = moment().format("YYYY-MM-DD"); // Get today's date

  useEffect(() => {
    document.title = `PrintRO_${hariini}`;

    // Fetch detail data
    const fetchData = async () => {
      if (idMp) {
        setLoading(true);
        try {
          // Fetch detail data
          const response = await axios.get(
            `${BaseUrlRace}sp/get-ro-detail?id_mp=${idMp}`,
            {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            }
          );
          const detailData = response.data.data[0];
          
          // If SjErlangga is not in detail data, try to get it from the list endpoint
          let sjErlangga = detailData?.SjErlangga;
          if (!sjErlangga && detailData?.detail?.length > 0) {
            // Try to get SjErlangga from detail array
            sjErlangga = detailData.detail[0]?.SjErlangga;
          }
          
          // If still not found, try fetching from list endpoint with keyword search
          if (!sjErlangga) {
            try {
              // Search using the SP number as keyword to find the item
              const spNumber = detailData?.sp;
              if (spNumber) {
                const listResponse = await axios.get(
                  `${BaseUrlRace}sp/get-ro-list?limit=100&page=1&keyword=${spNumber}&cabang=&sekolahTujuan=&startDate=&endDate=`,
                  {
                    headers: {
                      Authorization: localStorage.getItem("token"),
                    },
                  }
                );
                const matchingItem = listResponse.data?.data?.order?.find(
                  (item) => item.idMp === parseInt(idMp) || item.so === spNumber
                );
                if (matchingItem?.SjErlangga) {
                  sjErlangga = matchingItem.SjErlangga;
                  // Add SjErlangga to detailData and each detail item
                  detailData.SjErlangga = sjErlangga;
                  if (detailData.detail && detailData.detail.length > 0) {
                    detailData.detail = detailData.detail.map(item => ({
                      ...item,
                      SjErlangga: sjErlangga
                    }));
                  }
                }
              }
            } catch (listError) {
              console.warn("Could not fetch SjErlangga from list endpoint:", listError);
            }
          } else {
            // If SjErlangga exists at main level, add it to each detail item if not present
            if (sjErlangga && detailData.detail && detailData.detail.length > 0) {
              detailData.detail = detailData.detail.map(item => ({
                ...item,
                SjErlangga: item.SjErlangga || sjErlangga
              }));
            }
          }
          
          console.log("Detail data structure:", detailData);
          console.log("SjErlangga in detail:", detailData?.SjErlangga);
          console.log("SjErlangga in detail array:", detailData?.detail?.[0]?.SjErlangga);
          setDataDetail(detailData); // Set state → trigger useEffect kedua untuk barcode + print
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchData();
  }, [hariini, idMp]);

  // Generate barcode dan trigger window.print() setelah data tersedia di DOM
  useEffect(() => {
    if (!loading && dataDetail) {
      // Delay sedikit agar DOM (barcode) selesai render sebelum eksekusi JsBarcode & print
      setTimeout(() => {
        if (dataDetail?.sp) {
          const barcodeEl = document.querySelector("#barcode");
          if (barcodeEl) {
            JsBarcode("#barcode", dataDetail.sp, {
              format: "CODE128",
              lineColor: "#000000",
              width: 1.9,
              height: 50,
              displayValue: true,
            });
          }
        }
        window.print();
      }, 300);
    }
  }, [dataDetail, loading]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: "white" }}>
        <p style={{ fontSize: 16, color: "#666" }}>Memuat data...</p>
      </div>
    );
  }

  if (!dataDetail) {
    return <p>No data available</p>;
  }

  const detailRows = getPrintRows(dataDetail);
  const totalKoli = detailRows.reduce((acc, item) => acc + item.koli, 0);
  const totalIkat = detailRows.reduce((acc, item) => acc + item.ikat, 0);

  return (
    <div className="print-one-body" style={{ backgroundColor: "white" }}>
      <div className="container print-one mb-5">
        <div>
          <div className="d-flex justify-content-center">
            <Row style={{ width: "100%" }} gutter={[16, 16]}>
              <Col
                xs={6} sm={4} md={4}
                className="d-flex justify-content-start align-items-center"
              >
                <img
                  src="https://elogs.eurekalogistics.co.id/assets/admin/dist/img/logo-race.png"
                  alt="Logo"
                  style={{ maxWidth: "50%", maxHeight: "50%" }} // Ensure the image fits well
                />
              </Col>
              <Col
                xs={10} sm={12} md={12}
                className="d-flex justify-content-center align-items-center"
              >
                <div className="text-center">
                  <h2 style={{ fontSize: 16 }}>
                    <b>
                      {dataDetail?.retur === 0
                        ? "RETUR ORDER"
                        : "RETUR ORDER"}
                    </b>
                  </h2>
                </div>
              </Col>
              <Col
                xs={8} sm={8} md={8}
                className="d-flex justify-content-end align-items-end"
              >
                <div className="text-center">
                  <svg id="barcode"></svg> {/* Barcode here */}
                </div>
              </Col>
            </Row>
          </div>
        </div>

        <br />

        <div>
          <Row className="mt-3">
            <Col
              xs={12} sm={12} md={10}
              style={{ fontSize: 13, fontWeight: "bold" }}
            >
              <table>
                <tbody>
                  <tr>
                    <td className="nowrap">Nomor Retur</td>
                    <td className="mx-2">:</td>
                    <td>{dataDetail.sp || "-"}</td>
                  </tr>
                  <tr>
                    <td className="nowrap">No Referensi</td>
                    <td className="mx-2">:</td>
                    <td>
                      {dataDetail.noReferensi && dataDetail.noReferensi !== "-"
                        ? dataDetail.noReferensi
                        : "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="nowrap">Tanggal</td>
                    <td className="mx-2">:</td>
                    <td>{dataDetail.tgl_pickup || "-"}</td>
                  </tr>
                </tbody>
              </table>
            </Col>

            <Col
              xs={12} sm={12} md={12}
              style={{ fontSize: 13, fontWeight: "bold" }}
            >
              <table>
                <tbody>
                  <tr>
                    <td>Nama Driver</td>
                    <td className="mx-2">:</td>
                    <td>{dataDetail.detail?.[0]?.driver || "-"}</td>
                  </tr>
                  <tr>
                    <td>Plat Nomor</td>
                    <td className="mx-2">:</td>
                    <td>{dataDetail.detail?.[0]?.noPol || "-"}</td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>

          <Row className="mt-1" style={{ fontSize: 13, fontWeight: "bold" }}>
            <Col xs={24}>
              <table>
                <tbody>
                  <tr>
                    <td className="nowrap">Alamat</td>
                    <td className="mx-2">:</td>
                    <td>{getPrintAddress(dataDetail)}</td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
        </div>

        <table
          style={{ fontSize: 12, border: "2px solid black" }}
          className="table table-bordered mt-3"
        >
          <thead className="text-center">
            <tr>
              <th rowSpan="2" className="align-middle" style={{ width: "5%" }}>
                NO
              </th>
              <th rowSpan="2" className="align-middle" style={{ width: "35%" }}>
                NAMA PELANGGAN
              </th>
              <th rowSpan="2" className="align-middle" style={{ width: "35%" }}>
                MEMO
              </th>
              <th colSpan="2">JUMLAH</th>
            </tr>
            <tr>
              <th>COLLY</th>
              <th>IKAT</th>
            </tr>
          </thead>
          <tbody>
            {detailRows.map((item, index) => (
              <tr className="text-center" key={index}>
                <td>{index + 1}</td>
                <td>{item.sekolahTujuan}</td>
                <td>{item.memoText}</td>
                <td>{item.koli}</td>
                <td>{item.ikat}</td>
              </tr>
            ))}
            <tr>
              <td colSpan="3" style={{ fontWeight: "bold", fontSize: "16px" }}>
                TOTAL
              </td>
              <td className="text-center">
                {totalKoli}
              </td>
              <td className="text-center">
                {totalIkat}
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ fontSize: 16 }}>
          <b>Note :</b>
        </div>
        <div
          style={{ border: "2px solid black", padding: 10, textAlign: "start" }}
        >
          <p>
            <b>
              <i style={{ fontSize: 16, fontWeight: "bold" }}>
                Penarikan retur dilakukan hanya untuk barang yang telah terbungkus (ikat/koli), tersegel, berlabel, dan sesuai dengan jumlah yang tercantum pada dokumen retur. Raja Cepat tidak bertanggung jawab atas isi, kelengkapan, maupun kondisi barang setelah diterima oleh gudang penerima.
              </i>
            </b>
          </p>
        </div>

        <div className="row text-center mt-3">
          <Row>
            <Col span={8} style={{ fontSize: 12, fontWeight: "bold" }}>
              <p>Pelanggan</p>
              <p style={{ marginTop: 80 }}>
                (.............................................)
              </p>
              <p>
                <i>Nama, Tanda Tangan, Tanggal, dan Stempel</i>
              </p>
              <p>
                <i>No HP:............................................</i>
              </p>
            </Col>
            <Col span={8} style={{ fontSize: 12, fontWeight: "bold" }}>
              <p>Driver</p>
              <p style={{ marginTop: 80 }}>
                (.............................................)
              </p>
              <p>
                <i>Nama, Tanda Tangan, dan Tanggal</i>
              </p>
              <p>
                <i>No HP:............................................</i>
              </p>
            </Col>
            <Col span={8} style={{ fontSize: 12, fontWeight: "bold" }}>
              <p>Penerima</p>
              <p style={{ marginTop: 80 }}>
                (.............................................)
              </p>
              <p>
                <i>Nama, Tanda Tangan, dan Tanggal</i>
              </p>
              <p>
                <i>No HP:............................................</i>
              </p>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

export default PrintRO;
